/**
 * GDI HTML entity decoder
 *
 * Shared helper for decoding HTML entities emitted by CMS storage. Works
 * identically on the server and the client (no DOM dependency), so it is safe
 * to use during SSR without risking hydration mismatches.
 *
 * Handles:
 *   - Named entities from NAMED_HTML_ENTITIES (Latin accents used across the
 *     supported European locales plus common punctuation).
 *   - Numeric references, both decimal (&#233;) and hexadecimal (&#xE9;).
 */

/**
 * Named HTML entities that CMS storage may emit inside text.
 * Covers the Latin accented characters used across the supported European
 * locales (French, German, Spanish, Portuguese, Italian, …) plus common
 * punctuation. Numeric references (&#233; / &#xE9;) are handled separately.
 */
export const NAMED_HTML_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    aacute: 'á', Aacute: 'Á', agrave: 'à', Agrave: 'À', acirc: 'â', Acirc: 'Â',
    auml: 'ä', Auml: 'Ä', atilde: 'ã', Atilde: 'Ã', aring: 'å', Aring: 'Å', aelig: 'æ', AElig: 'Æ',
    ccedil: 'ç', Ccedil: 'Ç',
    eacute: 'é', Eacute: 'É', egrave: 'è', Egrave: 'È', ecirc: 'ê', Ecirc: 'Ê', euml: 'ë', Euml: 'Ë',
    iacute: 'í', Iacute: 'Í', igrave: 'ì', Igrave: 'Ì', icirc: 'î', Icirc: 'Î', iuml: 'ï', Iuml: 'Ï',
    ntilde: 'ñ', Ntilde: 'Ñ',
    oacute: 'ó', Oacute: 'Ó', ograve: 'ò', Ograve: 'Ò', ocirc: 'ô', Ocirc: 'Ô',
    ouml: 'ö', Ouml: 'Ö', otilde: 'õ', Otilde: 'Õ', oslash: 'ø', Oslash: 'Ø',
    uacute: 'ú', Uacute: 'Ú', ugrave: 'ù', Ugrave: 'Ù', ucirc: 'û', Ucirc: 'Û', uuml: 'ü', Uuml: 'Ü',
    yacute: 'ý', Yacute: 'Ý', yuml: 'ÿ', szlig: 'ß',
    laquo: '«', raquo: '»', hellip: '…', ndash: '–', mdash: '—',
    lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
    copy: '©', reg: '®', trade: '™', deg: '°', euro: '€', pound: '£', cent: '¢', middot: '·'
}

/**
 * Decode named and numeric HTML entities in a single pass.
 *
 * @param {string} str
 * @returns {string}
 */
export const decodeHtmlEntities = (str) =>
    String(str ?? '').replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (match, entity) => {
        if (entity[0] === '#') {
            const codePoint =
                entity[1] === 'x' || entity[1] === 'X'
                    ? parseInt(entity.slice(2), 16)
                    : parseInt(entity.slice(1), 10)
            return Number.isNaN(codePoint) ? match : String.fromCodePoint(codePoint)
        }
        return Object.prototype.hasOwnProperty.call(NAMED_HTML_ENTITIES, entity)
            ? NAMED_HTML_ENTITIES[entity]
            : match
    })
